import { type FitnessDomain, isParticipating } from '~/models/fitnessDomain'
import { type CombinedOpinion, combineDomainOpinions, type DomainOutcome } from '~/rules/combine'
import { domainRulesUnavailable, isDomainImplemented } from '~/rules/domainRegistry'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { listDomainGoals } from '~/storage/repositories/domainGoalRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import { toIsoTimestamp } from '~/types/dates'
import type { Result } from '~/utils/result'
import { ok } from '~/utils/result'
import { getWeeklyOutcome } from './rulesService'

/**
 * One outcome per participating domain, and the combined answer (D-090).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  WHY THIS IS A SERVICE AND NOT PART OF `rules/`
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Deciding which domains participate means reading `domainGoals`, and the
 *  layering guard forbids `rules/` from touching storage. The same split that put
 *  `evidenceSummary.ts` here: the pure part — ordering, capping, choosing the lead
 *  — is `combineDomainOpinions` in `rules/`, and this supplies it with plain
 *  values it can be fixture-tested against without a database.
 *
 *  It also reads the clock, which `rules/` may not, so `evaluatedAt` is stamped
 *  here and passed down.
 */

export interface DomainOpinions {
  readonly perDomain: readonly DomainOutcome[]
  readonly combined: CombinedOpinion
}

/**
 * Which domains this user is actually working on.
 *
 * The one read every adaptive screen needs, so the question "is cardio active?"
 * is answered in one place rather than by each screen filtering goals its own
 * way. Returns an empty list rather than a failure when there is no project: a
 * fresh install has no domains, which is a fact rather than an error.
 *
 * **`trackOnly` is included here and excluded from opinions**, and the difference
 * is deliberate. A track-only domain still collects evidence, so a screen asking
 * "should I show this?" wants it; the weekly opinion asking "should I judge this?"
 * does not.
 */
export async function getActiveDomains(
  db: SolaDatabase,
): Promise<Result<readonly FitnessDomain[], StorageError>> {
  const project = await getActiveProject(db)
  if (!project.ok) return project
  if (project.value === undefined) return ok([])

  const goals = await listDomainGoals(db, project.value.id)
  if (!goals.ok) return goals

  return ok(goals.value.filter(isParticipating).map((goal) => goal.domain))
}

/**
 * Evaluate this week across every domain the user is actually working on.
 *
 * **Only participating goals are included** — `isParticipating` excludes paused,
 * ended and `inactive` goals, and a `trackOnly` goal is excluded here as well:
 * track-only means "collect it and show it, plan nothing", so producing an
 * opinion about it would be exactly the work the user said they did not want.
 *
 * **A domain with no rules is not skipped.** It contributes a terminal outcome
 * carrying its own reason, so the user sees that Project Sola is not judging it
 * rather than being left to infer that silence means approval. That distinction
 * is the whole reason `DomainRulesUnavailable` exists as an outcome instead of
 * the domain simply being absent from the list.
 */
export async function getDomainOpinions(
  db: SolaDatabase,
  now: Date,
): Promise<Result<DomainOpinions, StorageError>> {
  const evaluatedAt = toIsoTimestamp(now)

  const project = await getActiveProject(db)
  if (!project.ok) return project

  /* No project means no domains, which is a fresh install rather than a failure.
     The combiner handles the empty list and reports `nothingToSay`. */
  if (project.value === undefined) {
    return ok({ perDomain: [], combined: combineDomainOpinions([]) })
  }

  const goals = await listDomainGoals(db, project.value.id)
  if (!goals.ok) return goals

  const participating = goals.value.filter(
    (goal) => isParticipating(goal) && goal.intention !== 'trackOnly',
  )

  const perDomain: DomainOutcome[] = []
  for (const goal of participating) {
    if (!isDomainImplemented(goal.domain)) {
      perDomain.push({
        domain: goal.domain,
        priority: goal.priority,
        outcome: domainRulesUnavailable(goal.domain, evaluatedAt),
      })
      continue
    }

    /* Body composition is the only implemented domain, and its engine is the one
       that has existed since Checkpoint 1.7 — unchanged, and reached through the
       same `getWeeklyOutcome` every earlier caller uses. A second path to it would
       be a second place for the phase context to be assembled differently. */
    const outcome = await getWeeklyOutcome(db, now)
    if (!outcome.ok) return outcome
    perDomain.push({ domain: goal.domain, priority: goal.priority, outcome: outcome.value })
  }

  return ok({ perDomain, combined: combineDomainOpinions(perDomain) })
}
